import React, { useState } from 'react';
import { submitFeedback } from '../../services/feedbackService';

/**
 * AIFeedbackModal
 *
 * Dedicated feedback form (not tied to a specific chat message) — separate
 * from ReportIssueModal.jsx, which handles crash/bug reports emailed to
 * support. This writes to the FeedbackEntry table via /api/feedback and
 * feeds the admin feedback analytics view.
 *
 * Usage:
 *   <AIFeedbackModal isOpen={showModal} onClose={() => setShowModal(false)} />
 */
const CATEGORIES = [
  { value: 'ai_quality', label: 'AI Quality' },
  { value: 'performance', label: 'Performance' },
  { value: 'ui_ux', label: 'UI / UX' },
  { value: 'features', label: 'Features' },
  { value: 'reliability', label: 'Reliability' },
  { value: 'other', label: 'Other' },
];

const AIFeedbackModal = ({ isOpen, onClose }) => {
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState('ai_quality');
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const isDarkMode = document.body.classList.contains('dark-mode');
  const bg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const fg = isDarkMode ? '#e0e0e0' : '#000000';
  const muted = isDarkMode ? '#666' : '#999';
  const borderColor = isDarkMode ? '#333' : '#e0e0e0';
  const inputBg = isDarkMode ? '#2d2d2d' : '#f5f5f5';
  const overlayBg = 'rgba(0, 0, 0, 0.5)';

  const reset = () => {
    setComment('');
    setCategory('ai_quality');
    setRating(0);
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);

    await submitFeedback({
      category,
      comment: comment.trim(),
      rating: rating > 0 ? rating : undefined,
    });

    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      reset();
      onClose();
    }, 1500);
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', backgroundColor: overlayBg,
        fontFamily: '"IBM Plex Mono", monospace',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: bg, color: fg, border: `1px solid ${borderColor}`,
          width: '100%', maxWidth: '440px', margin: '0 16px', boxShadow: '3px 3px 0 #000',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, backgroundColor: '#000',
          }}
        >
          <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#fff', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Send Feedback
          </h3>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
          >
            x
          </button>
        </div>

        <div style={{ padding: '18px' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ fontSize: '14px', fontWeight: '600' }}>Feedback sent. Thank you.</p>
              <p style={{ fontSize: '12px', color: muted, marginTop: '6px' }}>We read every submission.</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: muted, display: 'block', marginBottom: '6px' }}>
                  Category
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {CATEGORIES.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setCategory(value)}
                      style={{
                        padding: '6px 12px', fontSize: '11px', fontFamily: '"IBM Plex Mono", monospace',
                        fontWeight: category === value ? '700' : '400',
                        backgroundColor: category === value ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                        color: category === value ? (isDarkMode ? '#000' : '#fff') : muted,
                        border: `1px solid ${category === value ? 'transparent' : borderColor}`,
                        borderRadius: '0px', cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: muted, display: 'block', marginBottom: '6px' }}>
                  Rating (optional)
                </label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setRating(rating === n ? 0 : n)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                        fontSize: '18px', lineHeight: 1, color: n <= rating ? '#f59e0b' : muted,
                      }}
                      aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    >
                      {n <= rating ? '★' : '☆'}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: muted, display: 'block', marginBottom: '6px' }}>
                  Your feedback
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What's working well, or what should we improve?"
                  autoFocus
                  style={{
                    width: '100%', minHeight: '100px', padding: '10px', fontSize: '13px',
                    fontFamily: '"IBM Plex Mono", monospace', backgroundColor: inputBg, color: fg,
                    border: `1px solid ${borderColor}`, borderRadius: '0px', resize: 'vertical',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleClose}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontFamily: '"IBM Plex Mono", monospace',
                    backgroundColor: 'transparent', color: muted, border: `1px solid ${borderColor}`,
                    borderRadius: '0px', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!comment.trim() || submitting}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontFamily: '"IBM Plex Mono", monospace', fontWeight: '600',
                    backgroundColor: isDarkMode ? '#fff' : '#000', color: isDarkMode ? '#000' : '#fff',
                    border: 'none', borderRadius: '0px',
                    cursor: !comment.trim() || submitting ? 'not-allowed' : 'pointer',
                    opacity: !comment.trim() || submitting ? 0.5 : 1,
                  }}
                >
                  {submitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIFeedbackModal;
